import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Search, AlertCircle, Upload } from 'lucide-react';
import { productAPI, parseFileData } from '../utils/api';
import ProductModal from '../components/ProductModal';
import '../styles/Products.css';

function Products() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = products.filter(
        (p) =>
          p.productName.toLowerCase().includes(query) ||
          p.hsnCode.includes(query) ||
          p.category.toLowerCase().includes(query)
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }
  }, [searchQuery, products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getAll();
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      showAlert('Error fetching products', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productAPI.delete(id);
        showAlert('Product deleted successfully', 'success');
        fetchProducts();
      } catch (error) {
        showAlert('Error deleting product', 'error');
      }
    }
  };

  const handleSaveProduct = async (formData) => {
    try {
      if (editingProduct) {
        await productAPI.update(editingProduct.id, formData);
        showAlert('Product updated successfully', 'success');
      } else {
        await productAPI.create(formData);
        showAlert('Product created successfully', 'success');
      }
      fetchProducts();
      setShowModal(false);
    } catch (error) {
      showAlert('Error saving product', 'error');
    }
  };

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const fileInputRef = useRef(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const products = await parseFileData(file);
      await productAPI.bulkCreate(products);
      showAlert(`Successfully imported ${products.length} products`, 'success');
      fetchProducts();
    } catch (error) {
      showAlert('Error importing products: ' + error.message, 'error');
    } finally {
      setLoading(false);
      event.target.value = ''; // Reset file input
    }
  };

  const lowStockProducts = products.filter((p) => p.stockQuantity < 10);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Inventory Management</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={() => fileInputRef.current.click()}>
            <Upload size={18} />
            Import CSV/Excel
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <button className="btn btn-primary" onClick={handleAddProduct}>
            <Plus size={18} />
            Add Product
          </button>
        </div>
      </div>

      {alert && <div className={`alert alert-${alert.type}`}>{alert.message}</div>}

      {lowStockProducts.length > 0 && (
        <div className="alert alert-warning">
          <AlertCircle size={18} />
          <span>{lowStockProducts.length} products have low stock</span>
        </div>
      )}

      <div className="card">
            <div className="search-bar">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search by product name, HSN code, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state">
            <p>No products found</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Category</th>
                  <th>HSN Code</th>
                  <th>Purchase Price</th>
                  <th>Selling Price</th>
                  <th>Stock</th>
                  <th>Tax Rate</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.id} className={product.stockQuantity < 10 ? 'low-stock' : ''}>
                    <td>{product.productName}</td>
                    <td>{product.category}</td>
                    <td>{product.hsnCode}</td>
                    <td>₹{Number(product.purchasePrice ?? 0).toFixed(2)}</td>
                    <td>₹{Number(product.sellingPrice ?? 0).toFixed(2)}</td>
                    <td>
                      <span className={product.stockQuantity < 10 ? 'warning-stock' : ''}>
                        {Number(product.stockQuantity ?? 0).toFixed(2)}
                      </span>
                    </td>
                    <td>{product.taxRate}%</td>
                    <td className="action-buttons">
                      <button
                        className="btn-icon edit"
                        onClick={() => handleEditProduct(product)}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        className="btn-icon delete"
                        onClick={() => handleDeleteProduct(product.id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
  </div>

      {showModal && (
        <ProductModal
          product={editingProduct}
          onSave={handleSaveProduct}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export default Products;
